import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckMarkFillLegacy } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import {
  Box,
  Button,
  Dot,
  LinearProgress,
  Stack,
  StatusPill,
  Typography,
  TypographyVariants,
} from '@lyearn/ui';
import * as LearningContent from '@/modules/corporate/components/LearningContent';
import { ILearningContent } from '@/modules/corporate/components/LearningContent/types';
import { isRecurringSessionContent } from '@/modules/corporate/components/LearningContent/utils';
import ExternalContentProvider from '@/modules/corporate/modules/admin/views/LearningContent/components/ListItemCard/ExternalContentProvider';
import ContentConsumptionModal from '@/modules/journey/views/JourneyDetails/components/JourneyStep/components/ContentConsumptionModal';
import { PerformanceStatusEnum, ResourceTypeEnum } from '@/types/schema';

import JourneyParticipantProgressModal from '../../../../../../../journey/views/JourneyDetails/components/JourneyStep/components/JourneyParticipantProgressModal';

interface ContentDetailProps {
  content: ILearningContent;
  isOptional?: boolean;
  isMandatory?: boolean;
  isOptionalContentInPath?: boolean;
  nameVariant?: TypographyVariants;
  progress?: number;
  journeyStepStatus?: PerformanceStatusEnum;
  isNonParticipant?: boolean;
  batchId?: string;
  journeyStepId?: string;
  contentStatus?: PerformanceStatusEnum;
  overrides?: {
    contentCell?: {
      className?: string;
    };
  };
}

/**
 * Must be enclosed in <LearningContent> component
 * content must be in context
 * Ex:
 *  <LearningContent.Provider content={content}>
 *      <ContentDetail />
 *  </LearningContent>
 */

function ContentDetail(props: ContentDetailProps) {
  const { t } = useTranslation();
  const {
    content,
    isOptional,
    isMandatory,
    nameVariant,
    progress,
    journeyStepStatus,
    isNonParticipant,
    batchId,
    journeyStepId,
    isOptionalContentInPath,
    contentStatus,
    overrides,
  } = props;
  const [showProgressDialog, { toggle: toggleProgressDialog }] = useToggle();
  const [showConsumptionDialog, { toggle: toggleConsumptionDialog }] = useToggle();
  const renderProgressStatusButtons = () => {
    if (isNonParticipant) {
      if (journeyStepStatus === PerformanceStatusEnum.NotStarted) {
        return (
          <Button appearance="secondary" size="x-small" onClick={toggleConsumptionDialog}>
            {t('Common:Actions.Start')}
          </Button>
        );
      } else if (journeyStepStatus === PerformanceStatusEnum.InProgress) {
        return (
          <Stack alignItems="center">
            <Typography
              className="hover:bg-bg-hover pl-8 pr-8 mr-4 py-4 rounded-8 text-link-internal"
              variant="heading-xxs"
              onClick={toggleProgressDialog}>
              {t('Journey:ViewProgress')}
            </Typography>
            <Button appearance="secondary" size="x-small" onClick={toggleConsumptionDialog}>
              {t('Common:Actions.Continue')}
            </Button>
          </Stack>
        );
      } else if (journeyStepStatus === PerformanceStatusEnum.Completed) {
        return (
          <Stack alignItems="center" direction="row">
            <CheckMarkFillLegacy fillColorToken="icon-success" height={20} width={20} />
            <Typography className="text-text-secondary pl-4" variant="body-short-02">
              {t('Common:Filter.Completed')}
            </Typography>
          </Stack>
        );
      }
    }
    return progress ? (
      <Stack alignItems="end" direction="col">
        <Typography className="text-text-secondary pb-8" variant="body-short-03">
          {t('Common:ProgressInPercent', {
            progress,
          })}
        </Typography>
        <LinearProgress color="success" value={progress} width={120} />
      </Stack>
    ) : null;
  };

  return (
    <Box className="flex flex-1 items-center truncate me-8">
      <Stack alignItems="center" className="w-full justify-between" direction="row">
        <Stack className={cn(overrides?.contentCell?.className || '')}>
          <LearningContent.Icon className="h-48 w-48 flex-shrink-0 me-20" height={48} width={48} />
          <Box className="truncate">
            <Stack alignItems="center" className="mb-4 truncate" gapX={8}>
              <LearningContent.Name className="truncate" variant={nameVariant} />
              <LearningContent.RecurringEventIcon />
              <LearningContent.EventStatus />
              {isOptionalContentInPath ? (
                <StatusPill
                  appearance="secondary"
                  label={t('Common:Optional')}
                  size="medium"
                  variant="light"
                />
              ) : null}
            </Stack>
            <Box className="flex items-center">
              {content.type === ResourceTypeEnum.ExternalContent ? (
                <>
                  <ExternalContentProvider content={content} />
                </>
              ) : (
                <LearningContent.Type variant="body-short-02" />
              )}
              {content.sharedByWorkspace ? (
                <>
                  <Typography className="ms-4" color="text-text-secondary" variant="body-short-02">
                    {t('Admin:From')}
                  </Typography>
                  &nbsp; <LearningContent.CreatedBy />
                </>
              ) : null}
              <Dot />
              {isRecurringSessionContent(content.type) ? (
                <LearningContent.RecurrenceSchedule />
              ) : (
                <LearningContent.Duration variant="body-short-02" />
              )}

              {isMandatory ? (
                <>
                  <Dot />
                  <Typography
                    className="italic pe-8"
                    color="text-text-secondary"
                    variant="body-short-02">
                    {t('Common:Mandatory')}
                  </Typography>
                </>
              ) : null}

              {isOptional ? (
                <>
                  <Dot />
                  <Typography
                    className="italic pe-8"
                    color="text-text-secondary"
                    variant="body-short-02">
                    {t('Common:Optional')}
                  </Typography>
                </>
              ) : null}
            </Box>
          </Box>
        </Stack>
        {renderProgressStatusButtons()}
        {showProgressDialog ? (
          <JourneyParticipantProgressModal
            batchId={batchId ?? ''}
            content={content}
            journeyStepId={journeyStepId ?? ''}
            onClose={toggleProgressDialog}
          />
        ) : null}
        {showConsumptionDialog ? (
          <ContentConsumptionModal
            batchId={batchId ?? ''}
            content={content}
            journeyStepId={journeyStepId ?? ''}
            onClose={toggleConsumptionDialog}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

export default ContentDetail;
